/**
 * Fit-area insets, measured from real chrome elements.
 * The fit area is the webview minus the left rail (left), the visible right
 * panel (right), and the space switcher (top). Bottom remains 0 — fitted
 * frames may render under the floating input bar by design.
 */
export interface ViewportInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

type FitInsetSide = "left" | "right" | "top";

function insetMinimumFor(el: Element): number {
  const parsed = Number.parseFloat(el.getAttribute("data-fit-inset-min") ?? "");
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/** For "left" elements we read `rect.right` (not width): the rail is
 *  always the rightmost left-side chrome element — it abuts or extends
 *  past the sidebar — so its right edge IS the fit area's left boundary.
 *  Symmetrically, "top" elements contribute `rect.bottom`; "right" elements
 *  contribute the distance from their left edge to the viewport's right edge. */
function maxInset(attr: FitInsetSide): number {
  let edge = 0;
  if (typeof document === "undefined") return edge;
  for (const el of document.querySelectorAll(`[data-fit-inset="${attr}"]`)) {
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) continue;
    const minimum = insetMinimumFor(el);
    if (attr === "right") {
      const viewportRight =
        typeof window === "undefined" ? 0 : window.innerWidth;
      edge = Math.max(edge, minimum, viewportRight - rect.left);
    } else {
      edge = Math.max(
        edge,
        minimum,
        attr === "left" ? rect.right : rect.bottom,
      );
    }
  }
  return Math.max(0, edge);
}

export function getViewportInsets(): ViewportInsets {
  return {
    top: maxInset("top"),
    bottom: 0,
    left: maxInset("left"),
    right: maxInset("right"),
  };
}
